// Match result card — shared between the live playtest page (/playtest)
// and the match history page (/playtest/history).
//
// Shows both decks side-by-side with win counts, Wald95 confidence
// intervals, and a progress bar. On the live page we pass the in-flight
// `completedGames` / `elapsedMs` so the bar animates during the run. On
// the history page we compute them from the completed job so the same
// layout renders statically.
//
// The `showDetailsLink` prop controls the "View details & summarize"
// button — it's redundant on the detail page itself (we're already there)
// so the history page, which wraps each card in a Link, also hides it.

import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { cn } from "../lib/cn";
import type { PlaytestJob, PlaytestOutcome } from "../lib/api";

/**
 * Wald-approximation 95% confidence interval for a proportion. At low n,
 * the interval is wide and cropped to [0,1]. Displayed alongside the
 * win rate so the user doesn't over-read "70% on 10 games" as a strong
 * signal. This is honesty math, not a spec claim.
 */
export function wald95(wins: number, n: number): {
  p: number;
  lo: number;
  hi: number;
  half: number;
} {
  if (n <= 0) return { p: 0, lo: 0, hi: 0, half: 0 };
  const p = wins / n;
  const half = 1.96 * Math.sqrt((p * (1 - p)) / n);
  return {
    p,
    lo: Math.max(0, p - half),
    hi: Math.min(1, p + half),
    half,
  };
}

export interface MatchResultsProps {
  job: PlaytestJob;
  /** Live outcome for in-flight jobs. Defaults to job.outcome, which is
   *  correct for completed/errored history rows. */
  live?: PlaytestOutcome;
  /** Games completed including DNFs. Defaults to
   *  games_played + dnf from job.outcome. */
  completedGames?: number;
  /** Wall-clock elapsed. For history, pass elapsed_ms from HistoryRow.
   *  Defaults to 0 (the progress meter still works for complete jobs
   *  via the status check). */
  elapsedMs?: number;
  /** Show the "View details & summarize" link. Default true for the
   *  live playtest page; pass false when the card is itself inside a
   *  link to the detail page (history). */
  showDetailsLink?: boolean;
}

export default function MatchResults({
  job,
  live: liveProp,
  completedGames: completedProp,
  elapsedMs: elapsedProp,
  showDetailsLink = true,
}: MatchResultsProps) {
  const live = liveProp ?? job.outcome;
  const completedGames = completedProp ?? live.games_played + live.dnf;
  const elapsedMs = elapsedProp ?? 0;

  const played = live.games_played;
  const aRate = wald95(live.deck_a_wins, played);
  const bRate = wald95(live.deck_b_wins, played);
  const progressPct = job.games > 0
    ? Math.min(100, (completedGames / job.games) * 100)
    : 0;

  // Once complete, highlight the winning side (by raw wins; ties are
  // intentionally un-highlighted — draws mean neither deck "won").
  const winner: "a" | "b" | "tie" | null =
    job.status === "complete"
      ? live.deck_a_wins > live.deck_b_wins
        ? "a"
        : live.deck_b_wins > live.deck_a_wins
          ? "b"
          : "tie"
      : null;

  return (
    <section className="surface p-6 space-y-5">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
          {job.status === "complete"
            ? "Match complete"
            : job.status === "error"
              ? "Match errored"
              : "Match in progress"}
        </h2>
        <div className="text-[11px] font-mono tabular-nums text-fg-subtle">
          {completedGames} / {job.games} games
          {elapsedMs > 0 && <> · {(elapsedMs / 1000).toFixed(0)}s</>}
          {job.forge_version && <> · forge {job.forge_version}</>}
        </div>
      </header>

      <div className="grid gap-5 md:grid-cols-2">
        <SideCard
          label="Deck A"
          theme={job.deck_a_theme}
          wins={live.deck_a_wins}
          rate={aRate}
          played={played}
          highlight={winner === "a"}
        />
        <SideCard
          label="Deck B"
          theme={job.deck_b_theme}
          wins={live.deck_b_wins}
          rate={bRate}
          played={played}
          highlight={winner === "b"}
        />
      </div>

      <div className="flex flex-wrap gap-4 text-xs font-mono tabular-nums text-fg-muted">
        <span>Draws: {live.draws}</span>
        <span>DNF: {live.dnf}</span>
        <span>Played: {played}</span>
      </div>

      <div className="space-y-1">
        <div className="h-1.5 w-full rounded-full bg-bg-raised overflow-hidden">
          <div
            className="h-full bg-accent transition-[width] duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="text-[11px] text-fg-subtle font-mono tabular-nums">
          {progressPct.toFixed(0)}%
        </div>
      </div>

      {job.status === "error" && (
        <div className="text-xs text-danger">{job.error ?? "Unknown error"}</div>
      )}

      {job.status === "complete" && winner === "tie" && (
        <div className="text-[11px] text-fg-subtle">
          Dead tie. Re-run at higher N if you want to break it.
        </div>
      )}

      {showDetailsLink && job.status === "complete" && (
        <div className="pt-2">
          <Link
            to={`/playtest/jobs/${encodeURIComponent(job.id)}`}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-border bg-bg-raised hover:bg-bg-raised/70"
          >
            <Sparkles size={12} strokeWidth={1.75} />
            View details &amp; summarize
          </Link>
        </div>
      )}
    </section>
  );
}

function SideCard({
  label,
  theme,
  wins,
  rate,
  played,
  highlight,
}: {
  label: string;
  theme: string;
  wins: number;
  rate: { p: number; lo: number; hi: number; half: number };
  played: number;
  highlight: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-md border p-4 transition-colors",
        highlight ? "border-accent bg-accent/5" : "border-border",
      )}
    >
      <div className="text-[11px] uppercase tracking-wider text-fg-subtle">
        {label}
        {highlight && <span className="ml-2 text-accent">· winner</span>}
      </div>
      <div className="text-sm font-semibold tracking-tight truncate mt-0.5">
        {theme}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-mono text-4xl tabular-nums">{wins}</div>
        <div className="text-xs text-fg-subtle">wins</div>
      </div>
      {played > 0 && (
        <div className="mt-2 font-mono text-xs tabular-nums text-fg-muted">
          {(rate.p * 100).toFixed(0)}%
          <span className="text-fg-subtle"> (± {(rate.half * 100).toFixed(0)}%)</span>
        </div>
      )}
      {played === 0 && (
        <div className="mt-2 font-mono text-xs text-fg-subtle">no games yet</div>
      )}
    </div>
  );
}
